/** Small presentational primitives shared across portal pages. */
import React, { useState } from 'react';
import { CvsiError } from '@cvsi/shared';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="topbar">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="muted">{subtitle}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="card">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ marginBottom: 10, width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function ErrorView({ error, onRetry }: { error: Error | CvsiError; onRetry?: () => void }) {
  const isAuth = error instanceof CvsiError && (error.kind === 'unauthenticated' || error.kind === 'forbidden');
  return (
    <div className="error">
      <strong>{error instanceof CvsiError ? error.kind : 'error'}</strong>: {error.message}
      {isAuth && <div className="muted" style={{ marginTop: 6 }}>Your session may lack the required scope, or has expired.</div>}
      {onRetry && <div style={{ marginTop: 10 }}><button className="secondary small" onClick={onRetry}>Retry</button></div>}
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const cls = ['active', 'verified', 'good', 'resolved', 'delivered', 'ok', 'low', 'accepted', 'cleared'].includes(status)
    ? 'good'
    : ['suspended', 'failed', 'bad', 'revoked', 'closed', 'degraded', 'review_required', 'manual_review', 'rejected', 'confirmed_issue'].includes(status)
      ? 'bad'
      : ['elevated', 'escalated'].includes(status)
        ? 'warn'
        : ['monitor'].includes(status)
          ? 'monitor'
          : ['sandbox', 'pending', 'open', 'warn'].includes(status)
            ? 'warn'
            : '';
  return <span className={`pill ${cls}`}>{status.replace(/_/g, ' ')}</span>;
}

export function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export function CopyableSecret({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      {label && <label>{label}</label>}
      <div className="secret">{value}</div>
      <div style={{ marginTop: 8 }}>
        <button
          className="secondary small"
          onClick={async () => {
            try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
          }}
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="muted" style={{ padding: '14px 2px' }}>{children}</div>;
}

/**
 * Honest placeholder for a widget with no real data source behind it yet. Never fake the visual
 * with sample data — say plainly what's missing and, where known, what would connect it.
 */
export function PlaceholderPanel({ title, message }: { title: string; message: string }) {
  return (
    <div className="placeholder-panel">
      <div className="ph-title">{title}</div>
      <div className="ph-body">{message}</div>
    </div>
  );
}
